// ============================================================================
// Entorno de la app (local | dev | prod) — FUENTE ÚNICA.
//
// Antes esto vivía duplicado en dos sitios con reglas distintas: App.tsx
// (VITE_APP_ENV + hostname) y developer-logs.tsx (su propia heurística de
// hostname). Dos mecanismos que pueden desalinearse, y el que decidía si
// mostrar los botones de "generar datos falsos" era el más débil de los dos.
//
// Regla de decisión, en orden:
//   1. VITE_APP_ENV inyectada en el build (build-arg del Dockerfile /
//      variable de compose). Es lo que se debe usar siempre.
//   2. localhost / 127.0.0.1 / [::1] → local.
//   3. subdominio que empieza por 'dev.' → dev.
//   4. cualquier otra cosa → PROD.
//
// El paso 4 es deliberado y es lo contrario de lo que hacía el código viejo,
// que caía en 'local' ante un host desconocido. Un despliegue en un dominio
// nuevo sin VITE_APP_ENV quedaba clasificado como local y mostraba los botones
// de sembrar datos de prueba sobre la operación REAL. El costo de equivocarse
// hacia 'prod' es que en un entorno de pruebas raro falte el banner; el costo
// de equivocarse hacia 'local' es sembrar facturas falsas en producción.
// ============================================================================

export type AppEnv = 'local' | 'dev' | 'prod';

/** Host de producción. Configurable por si cambia el dominio. */
const PROD_HOST = (import.meta.env.VITE_PROD_HOST as string | undefined)
  || 'salonika.tmarketingsoftware.com';

const HOSTS_LOCALES = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0']);

export function resolveAppEnv(): AppEnv {
  const explicit = import.meta.env.VITE_APP_ENV;
  if (explicit === 'local' || explicit === 'dev' || explicit === 'prod') return explicit;

  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (HOSTS_LOCALES.has(host)) return 'local';
  if (host.startsWith('dev.')) return 'dev';
  if (host === PROD_HOST) return 'prod';
  // Host desconocido: se asume producción. Ver la nota de arriba.
  return 'prod';
}

/**
 * ¿Se pueden mostrar acciones destructivas o de sembrado de datos falsos?
 * Solo fuera de producción. Cualquier duda cuenta como producción.
 */
export function permiteDatosDePrueba(): boolean {
  return resolveAppEnv() !== 'prod';
}
